import { Injectable, signal } from '@angular/core'
import { APP_VERSION } from './app-version'

/**
 * VersionCheckService — detects that a *newer* build was deployed while a tab
 * is still running the old bundle.
 *
 * The running bundle knows its own version via the baked-in `APP_VERSION`
 * constant. The deployed `version.json` (served at the site root) always
 * reflects the *latest* build. Polling that file and comparing lets a stale
 * tab discover it needs to refresh — long-lived SPA sessions otherwise never
 * pick up new code and can silently break against a changed backend.
 *
 * On mismatch we flip `updateAvailable` (which the AppComponent renders as a
 * blocking modal) and stop polling — the decision is terminal; nothing walks
 * it back except a page reload.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  /** True once a newer deployed version has been observed. Latches on. */
  readonly updateAvailable = signal(false)

  private started = false
  private intervalId: ReturnType<typeof setInterval> | null = null

  // Bound listeners kept as fields so we can removeEventListener the exact refs.
  private readonly onFocus = () => { void this.check() }
  private readonly onVisibility = () => {
    if (document.visibilityState === 'visible') void this.check()
  }

  /**
   * Begins update detection. Idempotent — safe to call from AppComponent init
   * even if that runs more than once. Polls every 60s and additionally checks
   * whenever the tab regains focus / becomes visible (the moments a user is
   * most likely to be returning to a long-idle tab).
   */
  start(): void {
    if (this.started) return
    this.started = true

    this.intervalId = setInterval(() => { void this.check() }, 60_000)
    window.addEventListener('focus', this.onFocus)
    document.addEventListener('visibilitychange', this.onVisibility)

    // Kick off an immediate check so a tab loaded against an already-superseded
    // build doesn't have to wait a full poll interval to notice.
    void this.check()
  }

  /** Tears down all polling/listeners. Called once an update is detected. */
  private stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    window.removeEventListener('focus', this.onFocus)
    document.removeEventListener('visibilitychange', this.onVisibility)
  }

  /**
   * Fetches the deployed version.json and compares it to the running bundle.
   * All errors are swallowed — a failed fetch (offline, transient 5xx) simply
   * means "try again next tick", never a thrown/leaked rejection.
   */
  private async check(): Promise<void> {
    try {
      // Cache-buster + no-store so we never read a stale CDN/browser copy.
      const res = await fetch('version.json?t=' + Date.now(), { cache: 'no-store' })
      if (!res.ok) return

      const { version } = await res.json() as { version?: string }
      if (version && version !== APP_VERSION) {
        this.updateAvailable.set(true)
        this.stop()
      }
    } catch {
      // Ignore — retried on the next interval / focus / visibility event.
    }
  }
}
